import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import {
  getLiquidacionesComisiones,
} from "../helpers/rutaComisiones";

import logo from "../images/ridersmiami-logo.png";

/* =========================================================
   FORMATOS
   ========================================================= */

const formatUSD = (value) => {
  const n = Number(value || 0);

  return n.toLocaleString("es-AR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
};

const formatUSDPdf = (value) => {
  const n = Number(value || 0);

  return `US$ ${n.toLocaleString(
    "es-AR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )}`;
};

const cargarImagen = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();

    img.crossOrigin = "Anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const getId = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    return value;
  }

  return value._id || value.id || "";
};

const nombreVendedor = (item) => {
  const vendedor = item?.vendedor;

  if (!vendedor) {
    return "-";
  }

  if (typeof vendedor === "string") {
    return vendedor;
  }

  return (
    [
      vendedor.nombres,
      vendedor.apellidos,
    ]
      .filter(Boolean)
      .join(" ") ||
    vendedor.email ||
    "-"
  );
};

/* =========================================================
   COMPONENTE
   ========================================================= */

export default function ReporteComisionesVendedores() {
  const [desde, setDesde] =
    useState("");

  const [hasta, setHasta] =
    useState("");

  const [sede, setSede] =
    useState("");

  const [vendedor, setVendedor] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [
    liquidaciones,
    setLiquidaciones,
  ] = useState([]);

  const [
    vendedoresBase,
    setVendedoresBase,
  ] = useState([]);

  /* =======================================================
     VENDEDORES DISPONIBLES
     ======================================================= */

  const vendedoresDisponibles =
    useMemo(() => {
      const map = new Map();

      vendedoresBase.forEach(
        (item) => {
          const id =
            getId(item?.vendedor);

          const nombre =
            nombreVendedor(item);

          if (
            id &&
            nombre &&
            !map.has(id)
          ) {
            map.set(id, nombre);
          }
        }
      );

      return Array.from(
        map.entries()
      )
        .map(([id, nombre]) => ({
          id,
          nombre,
        }))
        .sort((a, b) =>
          a.nombre.localeCompare(
            b.nombre,
            "es"
          )
        );
    }, [vendedoresBase]);

  /* =======================================================
     AGRUPAR POR VENDEDOR + SEDE
     ======================================================= */

  const filas =
    useMemo(() => {
      const map = new Map();

      liquidaciones.forEach(
        (item) => {
          const estado =
            String(
              item?.estado || ""
            ).toUpperCase();

          /*
            Las anuladas no forman parte
            del resumen económico.
          */
          if (estado === "ANULADA") {
            return;
          }

          const vendedorId =
            getId(
              item?.vendedor
            );

          const nombre =
            nombreVendedor(
              item
            );

          const sedeItem =
            item?.sede || "-";

          const tipo =
            String(
              item?.tipo || ""
            ).toUpperCase();

          const clave =
            `${vendedorId}-${sedeItem}`;

          if (!map.has(clave)) {
            map.set(clave, {
              vendedorId,

              vendedor:
                nombre,

              sede:
                sedeItem,

              cantidadLiquidaciones:
                0,

              cantidadProductos:
                0,

              comisionProductosUSD:
                0,

              bonosSemanalesUSD:
                0,

              bonosMensualesUSD:
                0,

              totalGeneradoUSD:
                0,

              totalPagadoUSD:
                0,

              saldoPendienteUSD:
                0,
            });
          }

          const grupo =
            map.get(clave);

          grupo.cantidadLiquidaciones +=
            1;

          /*
            IMPORTANTE:

            La liquidación mensual vuelve
            a contener cantidadProductos
            como referencia del ciclo.

            Para no contar dos veces los
            mismos productos, los productos
            y su comisión se toman solamente
            de liquidaciones SEMANALES.
          */
          if (tipo === "SEMANAL") {
            grupo.cantidadProductos +=
              Number(
                item
                  ?.cantidadProductos ||
                  0
              );

            grupo.comisionProductosUSD +=
              Number(
                item
                  ?.comisionProductosUSD ||
                  0
              );

            grupo.bonosSemanalesUSD +=
              Number(
                item
                  ?.bonoSemanalUSD ||
                  0
              );
          }

          if (tipo === "MENSUAL") {
            grupo.bonosMensualesUSD +=
              Number(
                item
                  ?.bonoMensualUSD ||
                  0
              );
          }

          grupo.totalGeneradoUSD +=
            Number(
              item
                ?.totalLiquidacionUSD ||
                0
            );

          grupo.totalPagadoUSD +=
            Number(
              item
                ?.totalPagadoUSD ||
                0
            );

          grupo.saldoPendienteUSD +=
            Number(
              item
                ?.saldoPendienteUSD ||
                0
            );
        }
      );

      return Array.from(
        map.values()
      )
        .map((item) => ({
          ...item,

          comisionProductosUSD:
            Number(
              item
                .comisionProductosUSD
                .toFixed(2)
            ),

          bonosSemanalesUSD:
            Number(
              item
                .bonosSemanalesUSD
                .toFixed(2)
            ),

          bonosMensualesUSD:
            Number(
              item
                .bonosMensualesUSD
                .toFixed(2)
            ),

          totalGeneradoUSD:
            Number(
              item
                .totalGeneradoUSD
                .toFixed(2)
            ),

          totalPagadoUSD:
            Number(
              item
                .totalPagadoUSD
                .toFixed(2)
            ),

          saldoPendienteUSD:
            Number(
              item
                .saldoPendienteUSD
                .toFixed(2)
            ),
        }))
        .sort(
          (a, b) =>
            b.totalGeneradoUSD -
            a.totalGeneradoUSD
        );
    }, [liquidaciones]);

  /* =======================================================
     TOTALES GENERALES
     ======================================================= */

  const totales =
    useMemo(() => {
      return filas.reduce(
        (acc, item) => {
          acc.cantidadVendedores +=
            1;

          acc.cantidadProductos +=
            item
              .cantidadProductos;

          acc.comisionProductosUSD +=
            item
              .comisionProductosUSD;

          acc.bonosSemanalesUSD +=
            item
              .bonosSemanalesUSD;

          acc.bonosMensualesUSD +=
            item
              .bonosMensualesUSD;

          acc.totalGeneradoUSD +=
            item
              .totalGeneradoUSD;

          acc.totalPagadoUSD +=
            item
              .totalPagadoUSD;

          acc.saldoPendienteUSD +=
            item
              .saldoPendienteUSD;

          return acc;
        },
        {
          cantidadVendedores: 0,
          cantidadProductos: 0,
          comisionProductosUSD: 0,
          bonosSemanalesUSD: 0,
          bonosMensualesUSD: 0,
          totalGeneradoUSD: 0,
          totalPagadoUSD: 0,
          saldoPendienteUSD: 0,
        }
      );
    }, [filas]);

  /* =======================================================
     CARGAR
     ======================================================= */

  const cargarReporte = async (
    cargaInicial = false
  ) => {
    setLoading(true);
    setError("");

    try {
      const data =
        await getLiquidacionesComisiones({
          desde,
          hasta,
          sede,
          vendedor,
        });

      const registros =
        Array.isArray(
          data?.liquidaciones
        )
          ? data.liquidaciones
          : [];

      setLiquidaciones(
        registros
      );

      if (cargaInicial) {
        setVendedoresBase(
          registros
        );
      }
    } catch (err) {
      console.error(
        "Error resumen vendedores:",
        err
      );

      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo cargar el informe."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarReporte(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =======================================================
     LIMPIAR
     ======================================================= */

  const limpiarFiltros = () => {
    setDesde("");
    setHasta("");
    setSede("");
    setVendedor("");
  };

  /* =======================================================
     CSV
     ======================================================= */

  const generarCSV = () => {
    if (filas.length === 0) {
      return;
    }

    const encabezados = [
      "Vendedor",
      "Sede",
      "Productos",
      "Comision productos USD",
      "Bonos semanales USD",
      "Bonos mensuales USD",
      "Total generado USD",
      "Pagado USD",
      "Saldo USD",
    ];

    const escapar = (value) => {
      const texto =
        String(value ?? "");

      return `"${texto.replace(
        /"/g,
        '""'
      )}"`;
    };

    const registros =
      filas.map(
        (item) => [
          item.vendedor,
          item.sede,
          item.cantidadProductos,
          item.comisionProductosUSD,
          item.bonosSemanalesUSD,
          item.bonosMensualesUSD,
          item.totalGeneradoUSD,
          item.totalPagadoUSD,
          item.saldoPendienteUSD,
        ]
      );

    const contenido = [
      encabezados,
      ...registros,
    ]
      .map((fila) =>
        fila
          .map(escapar)
          .join(";")
      )
      .join("\n");

    const blob = new Blob(
      [
        "\uFEFF",
        contenido,
      ],
      {
        type:
          "text/csv;charset=utf-8;",
      }
    );

    const url =
      URL.createObjectURL(blob);

    const link =
      document.createElement("a");

    link.href = url;

    link.download =
      "resumen-comisiones-vendedores-riders.csv";

    document.body.appendChild(
      link
    );

    link.click();

    document.body.removeChild(
      link
    );

    URL.revokeObjectURL(url);
  };

  /* =======================================================
     PDF
     ======================================================= */

  const generarPDF = async () => {
    try {
      const doc =
        new jsPDF(
          "landscape",
          "mm",
          "a4"
        );

      const logoImg =
        await cargarImagen(
          logo
        );

      doc.addImage(
        logoImg,
        "PNG",
        10,
        8,
        34,
        12
      );

      doc.setFont(
        "helvetica",
        "bold"
      );

      doc.setFontSize(16);

      doc.text(
        "Resumen de Comisiones por Vendedor",
        50,
        14
      );

      doc.setFont(
        "helvetica",
        "normal"
      );

      doc.setFontSize(10);

      doc.text(
        "Riders Miami",
        50,
        20
      );

      doc.text(
        `Desde: ${
          desde || "Todas"
        }   Hasta: ${
          hasta || "Todas"
        }`,
        287,
        14,
        {
          align: "right",
        }
      );

      const vendedorNombre =
        vendedoresDisponibles.find(
          (item) =>
            item.id === vendedor
        )?.nombre || "Todos";

      doc.text(
        `Sede: ${
          sede || "Todas"
        } | Vendedor: ${vendedorNombre}`,
        287,
        20,
        {
          align: "right",
        }
      );

      doc.setDrawColor(180);

      doc.line(
        10,
        24,
        287,
        24
      );

      /* RESUMEN */

      const resumenY = 28;
      const boxW = 49;
      const boxH = 14;
      const gap = 3;

      const itemsResumen = [
        {
          label:
            "Vendedores",
          value:
            String(
              totales
                .cantidadVendedores
            ),
        },
        {
          label:
            "Productos",
          value:
            String(
              totales
                .cantidadProductos
            ),
        },
        {
          label:
            "Comisiones",
          value:
            formatUSDPdf(
              totales
                .comisionProductosUSD
            ),
        },
        {
          label:
            "Bonos",
          value:
            formatUSDPdf(
              totales
                .bonosSemanalesUSD +
                totales
                  .bonosMensualesUSD
            ),
        },
        {
          label:
            "Total generado",
          value:
            formatUSDPdf(
              totales
                .totalGeneradoUSD
            ),
        },
      ];

      itemsResumen.forEach(
        (item, i) => {
          const x =
            10 +
            i *
              (boxW + gap);

          doc.setDrawColor(
            210
          );

          doc.roundedRect(
            x,
            resumenY,
            boxW,
            boxH,
            1.5,
            1.5
          );

          doc.setFont(
            "helvetica",
            "normal"
          );

          doc.setFontSize(8);

          doc.text(
            item.label,
            x + 2,
            resumenY + 4.5
          );

          doc.setFont(
            "helvetica",
            "bold"
          );

          doc.setFontSize(10);

          doc.text(
            String(
              item.value
            ),
            x + 2,
            resumenY + 10
          );
        }
      );

      const body =
        filas.map(
          (item) => [
            item.vendedor,

            item.sede,

            String(
              item
                .cantidadProductos
            ),

            formatUSDPdf(
              item
                .comisionProductosUSD
            ),

            formatUSDPdf(
              item
                .bonosSemanalesUSD
            ),

            formatUSDPdf(
              item
                .bonosMensualesUSD
            ),

            formatUSDPdf(
              item
                .totalGeneradoUSD
            ),

            formatUSDPdf(
              item
                .totalPagadoUSD
            ),

            formatUSDPdf(
              item
                .saldoPendienteUSD
            ),
          ]
        );

      autoTable(doc, {
        startY: 48,

        theme: "grid",

        margin: {
          left: 8,
          right: 8,
        },

        head: [
          [
            "Vendedor",
            "Sede",
            "Productos",
            "Comisión",
            "Bono semanal",
            "Bono mensual",
            "Total generado",
            "Pagado",
            "Saldo",
          ],
        ],

        body,

        foot: [
          [
            "Totales",
            "",
            String(
              totales
                .cantidadProductos
            ),

            formatUSDPdf(
              totales
                .comisionProductosUSD
            ),

            formatUSDPdf(
              totales
                .bonosSemanalesUSD
            ),

            formatUSDPdf(
              totales
                .bonosMensualesUSD
            ),

            formatUSDPdf(
              totales
                .totalGeneradoUSD
            ),

            formatUSDPdf(
              totales
                .totalPagadoUSD
            ),

            formatUSDPdf(
              totales
                .saldoPendienteUSD
            ),
          ],
        ],

        styles: {
          fontSize: 7,
          cellPadding: 1.1,
          valign: "middle",
        },

        headStyles: {
          fillColor: [
            33,
            37,
            41,
          ],
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },

        footStyles: {
          fillColor: [
            245,
            245,
            245,
          ],
          textColor: 20,
          fontStyle: "bold",
        },

        columnStyles: {
          0: {
            cellWidth: 38,
          },

          1: {
            cellWidth: 22,
          },

          2: {
            cellWidth: 20,
            halign: "center",
          },

          3: {
            cellWidth: 31,
            halign: "right",
          },

          4: {
            cellWidth: 31,
            halign: "right",
          },

          5: {
            cellWidth: 31,
            halign: "right",
          },

          6: {
            cellWidth: 33,
            halign: "right",
          },

          7: {
            cellWidth: 30,
            halign: "right",
          },

          8: {
            cellWidth: 28,
            halign: "right",
          },
        },

        didDrawPage: () => {
          const pageCount =
            doc.internal
              .getNumberOfPages();

          doc.setFontSize(8);

          doc.setTextColor(100);

          doc.text(
            `Página ${pageCount}`,
            doc.internal.pageSize
              .getWidth() - 10,
            doc.internal.pageSize
              .getHeight() - 6,
            {
              align: "right",
            }
          );
        },
      });

      doc.save(
        "resumen-comisiones-vendedores-riders.pdf"
      );
    } catch (error) {
      console.error(
        "Error generando PDF:",
        error
      );

      alert(
        "No se pudo generar el PDF."
      );
    }
  };

  /* =======================================================
     VISTA
     ======================================================= */

  return (
    <div className="container-fluid mt-3 mb-4">

      <Card className="shadow-sm border-0">

        <Card.Body>

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">

            <div>
              <h4 className="mb-0">
                Resumen de Comisiones
                por Vendedor
              </h4>

              <small className="text-muted">
                Riders Miami
              </small>
            </div>

            <div className="d-flex gap-2 flex-wrap">

              <Button
                variant="outline-secondary"
                size="sm"
                onClick={
                  limpiarFiltros
                }
              >
                Limpiar
              </Button>

              <Button
                variant="dark"
                size="sm"
                onClick={() =>
                  cargarReporte(false)
                }
                disabled={loading}
              >
                {loading
                  ? "Buscando..."
                  : "Buscar"}
              </Button>

              <Button
                variant="outline-success"
                size="sm"
                onClick={generarCSV}
                disabled={
                  loading ||
                  filas.length === 0
                }
              >
                CSV
              </Button>

              <Button
                variant="success"
                size="sm"
                onClick={generarPDF}
                disabled={
                  loading ||
                  filas.length === 0
                }
              >
                PDF
              </Button>

            </div>
          </div>

          {/* FILTROS */}

          <Row className="g-2 mb-2 align-items-end">

            <Col xs={12} sm={6} lg={3}>
              <Form.Label>
                Desde
              </Form.Label>

              <Form.Control
                type="date"
                value={desde}
                onChange={(e) =>
                  setDesde(
                    e.target.value
                  )
                }
                size="sm"
              />
            </Col>

            <Col xs={12} sm={6} lg={3}>
              <Form.Label>
                Hasta
              </Form.Label>

              <Form.Control
                type="date"
                value={hasta}
                onChange={(e) =>
                  setHasta(
                    e.target.value
                  )
                }
                size="sm"
              />
            </Col>

            <Col xs={12} sm={6} lg={3}>
              <Form.Label>
                Sede
              </Form.Label>

              <Form.Select
                value={sede}
                onChange={(e) =>
                  setSede(
                    e.target.value
                  )
                }
                size="sm"
              >
                <option value="">
                  Todas
                </option>

                <option value="TUCUMAN">
                  Tucumán
                </option>

                <option value="YERBA_BUENA">
                  Yerba Buena
                </option>

                <option value="MIAMI">
                  Miami
                </option>
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg={3}>
              <Form.Label>
                Vendedor
              </Form.Label>

              <Form.Select
                value={vendedor}
                onChange={(e) =>
                  setVendedor(
                    e.target.value
                  )
                }
                size="sm"
              >
                <option value="">
                  Todos
                </option>

                {vendedoresDisponibles.map(
                  (item) => (
                    <option
                      key={item.id}
                      value={item.id}
                    >
                      {item.nombre}
                    </option>
                  )
                )}
              </Form.Select>
            </Col>

          </Row>

          <div className="small text-muted mb-3">
            El resumen combina las
            liquidaciones semanales y
            mensuales sin duplicar los
            productos.
          </div>

          {error && (
            <div className="alert alert-danger py-2">
              {error}
            </div>
          )}

          {/* RESUMEN */}

          <Row className="g-2 mb-3">

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">

                  <small className="text-muted">
                    Vendedores
                  </small>

                  <h5 className="mb-0">
                    {
                      totales
                        .cantidadVendedores
                    }
                  </h5>

                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">

                  <small className="text-muted">
                    Productos
                  </small>

                  <h5 className="mb-0">
                    {
                      totales
                        .cantidadProductos
                    }
                  </h5>

                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">

                  <small className="text-muted">
                    Comisión productos
                  </small>

                  <h5 className="mb-0">
                    {formatUSD(
                      totales
                        .comisionProductosUSD
                    )}
                  </h5>

                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">

                  <small className="text-muted">
                    Bonos
                  </small>

                  <h5 className="mb-0">
                    {formatUSD(
                      totales
                        .bonosSemanalesUSD +
                        totales
                          .bonosMensualesUSD
                    )}
                  </h5>

                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">

                  <small className="text-muted">
                    Total generado
                  </small>

                  <h5 className="mb-0">
                    {formatUSD(
                      totales
                        .totalGeneradoUSD
                    )}
                  </h5>

                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">

                  <small className="text-muted">
                    Saldo
                  </small>

                  <h5 className="mb-0">
                    {formatUSD(
                      totales
                        .saldoPendienteUSD
                    )}
                  </h5>

                </Card.Body>
              </Card>
            </Col>

          </Row>

          {/* TABLA */}

          <div
            className="table-responsive"
            style={{
              maxHeight: "65vh",
            }}
          >

            <Table
              striped
              bordered
              hover
              size="sm"
              className="align-middle"
            >

              <thead
                className="table-dark"
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                }}
              >
                <tr>

                  <th>
                    Vendedor
                  </th>

                  <th>
                    Sede
                  </th>

                  <th className="text-center">
                    Productos
                  </th>

                  <th className="text-end">
                    Comisión
                  </th>

                  <th className="text-end">
                    Bono semanal
                  </th>

                  <th className="text-end">
                    Bono mensual
                  </th>

                  <th className="text-end">
                    Total generado
                  </th>

                  <th className="text-end">
                    Pagado
                  </th>

                  <th className="text-end">
                    Saldo
                  </th>

                  <th className="text-center">
                    Estado
                  </th>

                </tr>
              </thead>

              <tbody>

                {loading ? (
                  <tr>
                    <td
                      colSpan="10"
                      className="text-center py-4"
                    >
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-2"
                      />

                      Cargando informe...
                    </td>
                  </tr>
                ) : filas.length ===
                  0 ? (
                  <tr>
                    <td
                      colSpan="10"
                      className="text-center py-4 text-muted"
                    >
                      No existen
                      liquidaciones para
                      los filtros
                      seleccionados.
                    </td>
                  </tr>
                ) : (
                  filas.map(
                    (item) => (
                      <tr
                        key={`${item.vendedorId}-${item.sede}`}
                      >

                        <td className="fw-semibold">
                          {
                            item.vendedor
                          }
                        </td>

                        <td>
                          {
                            item.sede
                          }
                        </td>

                        <td className="text-center">
                          {
                            item
                              .cantidadProductos
                          }
                        </td>

                        <td className="text-end">
                          {formatUSD(
                            item
                              .comisionProductosUSD
                          )}
                        </td>

                        <td className="text-end">
                          {formatUSD(
                            item
                              .bonosSemanalesUSD
                          )}
                        </td>

                        <td className="text-end">
                          {formatUSD(
                            item
                              .bonosMensualesUSD
                          )}
                        </td>

                        <td className="text-end fw-bold">
                          {formatUSD(
                            item
                              .totalGeneradoUSD
                          )}
                        </td>

                        <td className="text-end text-success">
                          {formatUSD(
                            item
                              .totalPagadoUSD
                          )}
                        </td>

                        <td className="text-end fw-bold">
                          {formatUSD(
                            item
                              .saldoPendienteUSD
                          )}
                        </td>

                        <td className="text-center">
                          <Badge
                            bg={
                              item
                                .saldoPendienteUSD >
                              0
                                ? "warning"
                                : "success"
                            }
                          >
                            {item
                              .saldoPendienteUSD >
                            0
                              ? "Pendiente"
                              : "Al día"}
                          </Badge>
                        </td>

                      </tr>
                    )
                  )
                )}

              </tbody>

              {filas.length > 0 && (
                <tfoot>

                  <tr>

                    <th colSpan="2">
                      Totales
                    </th>

                    <th className="text-center">
                      {
                        totales
                          .cantidadProductos
                      }
                    </th>

                    <th className="text-end">
                      {formatUSD(
                        totales
                          .comisionProductosUSD
                      )}
                    </th>

                    <th className="text-end">
                      {formatUSD(
                        totales
                          .bonosSemanalesUSD
                      )}
                    </th>

                    <th className="text-end">
                      {formatUSD(
                        totales
                          .bonosMensualesUSD
                      )}
                    </th>

                    <th className="text-end">
                      {formatUSD(
                        totales
                          .totalGeneradoUSD
                      )}
                    </th>

                    <th className="text-end">
                      {formatUSD(
                        totales
                          .totalPagadoUSD
                      )}
                    </th>

                    <th className="text-end">
                      {formatUSD(
                        totales
                          .saldoPendienteUSD
                      )}
                    </th>

                    <th />

                  </tr>

                </tfoot>
              )}

            </Table>
          </div>

        </Card.Body>
      </Card>
    </div>
  );
}